import React, { useState } from 'react';
import { useLionAutoDiscord, useSaveLionAutoDiscord, useTestLionAutoDiscord } from '../../hooks/useLionAuto';
import { agoText } from '../../utils/lionAutoRunner';

// Discord alerts for Lion Auto, for super admins only (LaAutoPanel mounts it
// for them; the API refuses anyone else). One webhook for the whole server,
// stored encrypted with SKYBOX_CRED_KEY. The URL is write-only: once saved it
// is never shown again (the box clears), only the token's last 4 characters.
// Which events post to it is decided on the server; Send test posts one
// message and shows Discord's answer.

const WARNING_TEXT = {
  KEY_MISSING: "SKYBOX_CRED_KEY is not set on the server, so a webhook can't be saved or used.",
  WEBHOOK_UNREADABLE: "The saved webhook can't be decrypted (was SKYBOX_CRED_KEY changed?): paste it again.",
};

// A failed post's code (services/lionAuto/discord.js), in words
const SEND_ERROR_TEXT = {
  NOT_CONFIGURED: 'No webhook is saved.',
  HTTP_401: 'Discord refused the webhook (a wrong token): paste it again.',
  HTTP_403: 'Discord refused the webhook: paste a fresh one.',
  HTTP_404: 'Discord does not know this webhook (deleted, or a wrong id or token): paste a fresh one.',
  HTTP_429: 'Discord is rate limiting this webhook: try again in a minute.',
  TIMEOUT: 'Discord did not answer within 5 s: try again.',
  INTERNAL: 'The server could not send it (see the server log).',
};
const sendErrorText = (code) => SEND_ERROR_TEXT[code] || `Not sent (${code || 'unknown error'}).`;
const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

function LaDiscordCard() {
  const statusQ = useLionAutoDiscord();
  const saveM = useSaveLionAutoDiscord();
  const testM = useTestLionAutoDiscord();
  const [url, setUrl] = useState('');
  const [message, setMessage] = useState(null); // { kind: 'ok' | 'error', text } | null

  // Not a super admin after all (role changed since login): nothing to show
  if (statusQ.error?.response?.status === 403) return null;

  const s = statusQ.data || null;
  const warnings = Array.isArray(s?.warnings) ? s.warnings : [];
  const keyMissing = warnings.includes('KEY_MISSING') || s?.keyPresent === false;
  const busy = saveM.isPending || testM.isPending;

  let statusLine;
  if (statusQ.isLoading) statusLine = 'Reading…';
  else if (!s) statusLine = 'Could not read the Discord settings';
  else if (s.configured) statusLine = `Connected${s.hint ? ` "${s.hint}"` : ''} · last sent ${agoText(s.lastSentAt)}`;
  else statusLine = 'Not connected';

  const save = (webhookUrl, okText) => {
    setMessage(null);
    saveM.mutate(webhookUrl, {
      onSuccess: () => {
        setUrl('');
        setMessage({ kind: 'ok', text: okText });
      },
      onError: (err) => setMessage({ kind: 'error', text: errorOf(err, 'Saving the webhook failed.') }),
    });
  };
  const handleSave = (e) => {
    e.preventDefault();
    if (!url.trim() || busy || keyMissing) return;
    save(url.trim(), 'Saved. Send a test to check it.');
  };
  const handleRemove = () => {
    if (!window.confirm('Remove the Discord webhook? Lion Auto stops sending Discord alerts until one is saved again.')) return;
    save(null, 'Webhook removed.');
  };
  const handleTest = () => {
    setMessage(null);
    testM.mutate(undefined, {
      onSuccess: (r) => setMessage(r?.ok
        ? { kind: 'ok', text: 'Sent: check the Discord channel.' }
        : { kind: 'error', text: sendErrorText(r?.error) }),
      onError: (err) => setMessage({ kind: 'error', text: errorOf(err, 'The test could not be sent.') }),
    });
  };

  return (
    <section className="la-discord">
      <div className="la-panel-head">
        <h3>Discord alerts</h3>
        {s && (
          <span
            className={`la-run-chip ${s.configured ? 'ok' : 'warn'}`}
            title={s.configured ? 'Lion Auto can post to Discord' : 'No usable webhook: Lion Auto sends nothing to Discord'}
          >
            {s.configured ? 'CONNECTED' : 'NOT CONNECTED'}
          </span>
        )}
        <span className="la-muted">{statusLine}</span>
        {statusQ.error && !s && (
          <button type="button" className="btn btn-secondary btn-sm la-push-right" onClick={() => statusQ.refetch()}>Retry</button>
        )}
      </div>
      {warnings.map(w => <div key={w} className="la-status warn">{WARNING_TEXT[w] || w}</div>)}

      <form className="la-inline" onSubmit={handleSave}>
        <input
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          className="form-input form-input-sm la-discord-input"
          aria-label="Discord webhook URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={s?.hasWebhook ? 'saved — leave blank to keep' : 'https://discord.com/api/webhooks/…'}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={!url.trim() || busy || keyMissing}>
          {saveM.isPending ? 'Saving…' : 'Save'}
        </button>
        {s?.hasWebhook && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleRemove} disabled={busy}>Remove</button>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleTest}
          disabled={busy || !s?.configured}
          title={s?.configured ? 'Post one test message to the channel' : 'Save a webhook first'}
        >
          {testM.isPending ? 'Sending…' : 'Send test'}
        </button>
      </form>
      {message && <div className={`la-status${message.kind === 'error' ? ' error' : ''}`}>{message.text}</div>}
      {!message && s?.lastError && <div className="la-hint warn">Last post failed: {sendErrorText(s.lastError)}</div>}
      <div className="la-hint">
        Paste the channel's webhook URL (in Discord: channel settings → Integrations → Webhooks → Copy Webhook URL).
        It is write-only: once saved it is never shown again.
      </div>
    </section>
  );
}

export default LaDiscordCard;
